import { styled as atomSelect } from '@atom-web/core'
import CloseLine from '@atom-web/icons-24/CloseLine'
import EditBox from '@atom-web/icons-24/EditBox'
import Info from '@atom-web/icons-24/Info'
import { Dialog } from '@atom-web/macros'
import { Box, Button, Column, Flex, Row, Text } from '@atom-web/micros'
import { useRouter } from 'next/router'
import {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useReducer,
	useState,
} from 'react'
import OtpInput from 'react-otp-input'

import { triggerRquestCallback } from '../features/auth/actions/requestCallbackUser'
import triggerOTP from '../features/auth/actions/triggerOTP'
import isValidMobileNumber from '../features/auth/utils/isValidMobileNumber'
import AppContext from '../store/AppContext'
import getUserDetails from '../utils/actions/getUserDetails'
import { getLeadSource } from '../utils/analytics/leadSourceHelper'
import getImgIXUrl from '../utils/getImgIXUrl'
import isIxigoPartnersSite from '../utils/isIxigoPartnersSite'
import {
	EVENT_CHOOSE_LANGUAGE,
	EVENT_GET_OTP_NEW_USER,
	EVENT_MOB_NO_ENTERED_FOR_OTP,
	EVENT_MOBILE_NUMBER_RCB_CLICKED,
	EVENT_PROCEED_BOOK_NOW_CLICKED,
	EVENT_RE_SEND_OTP,
	trackEvent,
} from '../utils/ML/EventsTracker'
import { emailValidation } from '../utils/validations'
import { CHOOSE_LANGUAGE, OTP, SHOW_SUBMIT_SUCCESS } from './gcm/Constants'
import Input from './gcm/micros/Input'
import LanguageSelection from './LanguageSelection'

const StyledImage = atomSelect('img')

const CbrWrapper = ({
	vacationFlow = false,
	callback,
	regionCode = '',
	ctaText = '',
	type,
}) => {
	const router = useRouter()
	const [{ userDetails = {} }, appDispatch] = useContext(AppContext)
	const isIxigoPartnersSiteValue = isIxigoPartnersSite()

	//states
	const [visible, setVisible] = useState(false)
	const [showSubmitted, setShowSubmitted] = useState(true)
	const [otp, setOtp] = useState('')
	const [resendDisable, setResendDisable] = useState(true)
	const [counter, setCounter] = useState(20)
	const [otpResponse, setOtpResponse] = useState({
		id: '',
		message: '',
	})
	const [modalState, setModalState] = useState()
	const [otpError, setOtpError] = useState({
		error: false,
		status: '',
		message: '',
	})
	const [status, setStatus] = useState({
		isError: false,
		title: '',
		message: '',
	})

	const [value, setValue] = useReducer(
		(state, diff) => ({ ...state, ...diff }),
		{
			name: '',
			countryPhoneCode: '+91',
			mobileNumber: '',
			email: '',
			preferredLanguage: null,
		},
	)
	const { name, countryPhoneCode, mobileNumber, email, preferredLanguage } =
		value

	// Memos
	const modalName = useMemo(() => router.query?.m, [router])

	const isDefaultEmi = useMemo(() => router.query?.isdefaultemi, [router])
	const chosenEmi = useMemo(() => router.query?.chosenemi, [router])
	const emiAmount = useMemo(() => router.query?.emiamount, [router])

	const queryName = useMemo(() => router.query?.name, [router])
	const queryCountryPhoneCode = useMemo(
		() => router.query?.countryPhoneCode,
		[router],
	)
	const queryMobileNumber = useMemo(() => router.query?.mobileNumber, [router])
	const queryEmail = useMemo(() => router.query?.email, [router])
	const queryId = useMemo(() => router.query?.id, [router])

	// Conditions
	const isCBR = useMemo(() => modalName === 'cbr', [modalName])
	const isOTP = useMemo(() => modalName === 'cbr-otp', [modalName])
	const isLang = useMemo(() => modalName === 'cbr-lang', [modalName])

	const isLoggedIn = useMemo(() => userDetails?.loggedIn, [userDetails])
	const validation = useMemo(() => {
		let result = false
		if (
			userDetails &&
			name?.length &&
			isValidMobileNumber(countryPhoneCode + mobileNumber) &&
			emailValidation(email)
		) {
			result = true
		}
		return result
	}, [name, countryPhoneCode, mobileNumber, email, userDetails])

	const isLocalUser = useMemo(
		() => countryPhoneCode === '+91',
		[countryPhoneCode],
	)

	//side effects
	useEffect(() => {
		if (isCBR || isOTP || isLang) {
			setVisible(true)
			setShowSubmitted(false)
			if (isLang) {
				setModalState(CHOOSE_LANGUAGE)
			} else if (isOTP) {
				setModalState(OTP)
				triggerCounter()
			}
		}
	}, [modalName])

	useEffect(() => {
		if (userDetails?.loggedIn) {
			setValue({
				name: userDetails.name,
				countryPhoneCode: userDetails.countryPhoneCode,
				mobileNumber: userDetails.mobileNumber,
				email: userDetails.email,
			})
		}
	}, [userDetails, router])

	useEffect(() => {
		if (queryName?.length) {
			setValue({
				name: queryName,
			})
		}
		if (emailValidation(queryEmail)) {
			setValue({
				email: queryEmail,
			})
		}
		if (queryMobileNumber?.length && queryCountryPhoneCode?.length) {
			if (isValidMobileNumber(queryCountryPhoneCode + queryMobileNumber)) {
				setValue({
					countryPhoneCode: queryCountryPhoneCode,
					mobileNumber: queryMobileNumber,
				})
			}
		}
	}, [queryName, queryEmail, queryCountryPhoneCode, queryMobileNumber])

	//callbacks
	const clearAllIntervals = useCallback(() => {
		const interval_id = window.setInterval(function () {},
		Number.MAX_SAFE_INTEGER)
		for (let i = 1; i < interval_id; i++) {
			window.clearInterval(i)
		}
	}, [])

	const onClose = useCallback(() => {
		setVisible(false)
		clearAllIntervals()
		const path = router.asPath?.split('?')[0]
		const queryParams = { ...router.query }
		delete queryParams.m
		delete queryParams.isdefaultemi
		delete queryParams.chosenemi
		delete queryParams.emiamount
		delete queryParams.slug

		setModalState(null)

		router.push(
			{
				pathname: path,
				query: queryParams,
			},
			null,
			{ shallow: true, scroll: false },
		)
	}, [setVisible, router])

	const triggerCounter = useCallback(() => {
		let interval
		let count = 20
		setResendDisable(false)
		interval = setInterval(() => {
			if (count !== 0) {
				count -= 1
				setCounter(count)
			} else {
				setCounter(20)
				setResendDisable(true)
				clearInterval(interval)
			}
		}, 1000)
	}, [setResendDisable, setCounter])

	const resetCounter = useCallback(() => {
		setCounter(20)
	}, [setCounter])

	const backToCBR = useCallback(() => {
		setModalState(null)
	}, [])

	const triggerMobileOTP = useCallback(
		(isResend = false) => {
			const eventProps = {
				is_logged_in: false,
				leadSource: getLeadSource({
					router,
					lastLocation: {
						pathname: router.asPath,
					},
					emiInfo: {
						isDefaultEmi: isDefaultEmi ?? null,
						chosenEmi: chosenEmi ?? null,
						emiAmount: emiAmount ?? null,
					},
				}),
				ipAddress: sessionStorage.getItem('ipaddress') || '',
				regionCode,
				mobileNumber,
				countryPhoneCode,
			}
			if (isResend) {
				trackEvent(EVENT_RE_SEND_OTP, eventProps)
			} else if (name) {
				trackEvent(EVENT_GET_OTP_NEW_USER, eventProps)
			} else {
				trackEvent(EVENT_MOB_NO_ENTERED_FOR_OTP, eventProps)
			}
			// trackEvent(EVENT_MOB_NO_ENTERED_FOR_OTP, eventProps)

			const reqObj = {
				countryPhoneCode,
				mobileNumber,
				name,
			}

			triggerOTP({ reqObj }).then(async res => {
				if (res.data) {
					setOtpResponse({
						id: res.data.id,
						message: res.data.statusMessage,
					})

					if (!isResend && !res.data.preferredLanguage) {
						setModalState(CHOOSE_LANGUAGE)
					} else {
						setOtp('')
						setOtpError({
							error: false,
							status: '',
							message: '',
						})

						setModalState(OTP)
						triggerCounter()
					}
				}
			})
		},
		[setVisible, router, name, countryPhoneCode, mobileNumber, email],
	)

	const requestCallBack = useCallback(() => {
		const reqObj = {
			name,
			email,
			mobileNumber,
			countryPhoneCode,
			pageUrl: router.asPath.split('?')[0],
			regionCode,
			directToSales: vacationFlow,
			preferredLanguage,
			leadSource: getLeadSource({
				router,
				lastLocation: {
					pathname: router.asPath,
				},
				emiInfo: {
					isDefaultEmi: isDefaultEmi ?? null,
					chosenEmi: chosenEmi ?? null,
					emiAmount: emiAmount ?? null,
				},
				isCBR: true,
			}),
		}
		if (modalState === OTP) {
			reqObj.otp = otp
			reqObj.canSendWhatsAppMessages = true
			reqObj.id = otpResponse.id || queryId
		}
		if (type === 'jourenys' && ctaText) {
			trackEvent(EVENT_PROCEED_BOOK_NOW_CLICKED)
		}
		triggerRquestCallback({
			reqObj,
			router,
			userDetails,
			ignoreLogin: true,
			successSubmitCallback: res => {
				if (res.status === 'SUCCESS') {
					if (!callback) {
						setModalState(SHOW_SUBMIT_SUCCESS)
						setStatus({
							isError: false,
							title: 'We have received your details.',
							message: 'Our travel experts will get in touch with you shortly!',
						})
						getUserDetails({ dispatch: appDispatch })
					} else {
						onClose()

						callback()
					}
				}
			},
			failureSubmitCallback: res => {
				if (res.status === 'VERIFICATION_FAILED' || res.status === 'EXPIRED') {
					if (res.status === 'VERIFICATION_FAILED') {
						setOtpError({
							error: true,
							message: 'Wrong OTP Entered!',
						})
					} else {
						setOtpError({
							error: true,
							message: 'OTP Expired!',
						})
					}
				} else {
					setModalState(SHOW_SUBMIT_SUCCESS)
					setStatus({
						isError: true,
						title: 'Oops!',
						message: res.message,
					})
				}
			},
		})
	}, [
		name,
		email,
		mobileNumber,
		countryPhoneCode,
		router.asPath,
		regionCode,
		vacationFlow,
		callback,
		userDetails,
		otp,
		queryId,
		setShowSubmitted,
		setStatus,
		setOtpError,
	])

	const onChooseLanguage = language => {
		trackEvent(EVENT_CHOOSE_LANGUAGE, {
			language,
		})
		setValue({ preferredLanguage: language })

		setModalState(OTP)
	}

	let modelContent = null
	switch (modalState) {
		//common components goes here
		case CHOOSE_LANGUAGE:
			modelContent = (
				<Column
					css={{
						width: '100%',
						gap: '24px',
						'@lg': {
							height: '100%',
						},
					}}
				>
					<Row justify="between">
						<Column
							css={{
								alignItems: 'center',
								'@lg': { alignItems: 'flex-start' },
							}}
						>
							<Text
								css={{
									fontSize: '13px',
									lineHeight: '20px',
									color: '#036D4A',
									fontWeight: '$extrabold',
									textTransform: 'uppercase',
								}}
							>
								Before entering OTP...
							</Text>
							<Text
								css={{
									fontSize: '24px',
									lineHeight: '30px',
									color: '$black',
									fontWeight: '$semibold',
									textAlign: 'center',
									'@lg': {
										textAlign: 'left',
									},
								}}
							>
								Choose the language you're comfortable talking in.
							</Text>
						</Column>
						<CloseLine
							fill="#17181C"
							onClick={onClose}
							css={{
								cursor: 'pointer',
								width: '20px',
								height: '20px',
								display: 'none',
								'@lg': {
									display: 'block',
									width: '35px',
									height: '20px',
								},
							}}
						/>
					</Row>

					<LanguageSelection onChange={onChooseLanguage} />
				</Column>
			)
			break
		case OTP:
			modelContent = (
				<Column css={{ height: '100%', width: '100%' }}>
					<Row justify="between">
						<Box>
							<Text
								css={{
									fontSize: '24px',
									lineHeight: '30px',
									color: '$black',
									fontWeight: '$semibold',
								}}
							>
								Enter OTP
							</Text>

							<Text
								css={{
									fontSize: '$3',
									lineHeight: '$2',
									color: '$grey08',
									fontWeight: '$normal',
									paddingTop: '$1',
									'@lg': {
										paddingTop: '12px',
									},
								}}
							>
								{otpResponse.message || isLocalUser
									? 'OTP has been sent to your Mobile number'
									: 'OTP has been sent to your registered Email'}
							</Text>
							{isLocalUser && (
								<Row
									gap="1"
									align="center"
									css={{
										padding: '$1',
									}}
								>
									<Text
										css={{
											fontSize: '$3',
											lineHeight: '$2',
											color: '$black',
											fontWeight: '$normal',
										}}
									>
										{countryPhoneCode} {mobileNumber}
									</Text>
									<EditBox
										css={{
											width: '$4',
											height: '$4',
											paddingLeft: '$1',
											fill: '$grey12',
											cursor: 'pointer',
										}}
										onClick={() => {
											clearAllIntervals()
											resetCounter()
											backToCBR()
										}}
									/>
								</Row>
							)}
						</Box>
						<CloseLine
							cursor="pointer"
							fill="#333"
							onClick={onClose}
							css={{
								height: 22,
								width: 22,
							}}
						/>
					</Row>

					<Column
						css={{
							gap: '16px',
							paddingTop: '24px',
						}}
					>
						<Row css={{ width: '100%' }}>
							<OtpInput
								value={otp}
								onChange={e => setOtp(e)}
								numInputs={6}
								isInputNum
								shouldAutoFocus
								inputStyle={{
									minHeight: '46px',
									width: '42px',
									backgroundColor: '#F9F9F9',
									borderRadius: 8,
									textAlign: 'center',
									border: otpError.error
										? '1px solid #D73C54'
										: '1px solid #D8D8D8',
									fontSize: '20px',
									fontWeight: 600,
								}}
								containerStyle={{
									width: '100%',
									display: 'flex',
									justifyContent: 'space-between',
								}}
								focusStyle={{
									outline: 'none',
									border: isIxigoPartnersSiteValue
										? '1px solid #FF8C68'
										: '1px solid #09B367',
									borderRadius: '8px',
								}}
							/>
						</Row>
						<Row justify={otpError.error ? 'between' : 'end'}>
							{otpError.error && (
								<Row gap="1">
									<Info
										css={{
											fill: '$red06',
										}}
									/>
									<Text
										css={{
											fontSize: '13px',
											color: '$red06',
											lineHeight: '15px',
										}}
									>
										{otpError.status === 'VERIFICATION_FAILED'
											? 'Wrong OTP Entered!'
											: otpError.message || 'Wrong OTP Entered!'}
									</Text>
								</Row>
							)}
							{resendDisable ? (
								<Text
									css={{
										fontSize: '13px',
										fontWeight: '$bold',
										color: isIxigoPartnersSiteValue
											? '$primary06'
											: '$custom40',
										lineHeight: '$1',
										textAlign: 'end',
										cursor: 'pointer',
										textDecorationLine: 'underline',
										textDecorationThickness: '1px',
									}}
									onClick={() => triggerMobileOTP(true)}
								>
									Resend OTP
								</Text>
							) : (
								<Text
									css={{
										fontSize: '13px',
										fontWeight: '$bold',
										color: '$grey09',
										lineHeight: '$1',
										textAlign: 'end',
										cursor: 'not-allowed',
									}}
								>
									Resend OTP in {counter}
								</Text>
							)}
						</Row>
					</Column>
					<Column
						css={{
							flex: 1,
							justifyContent: 'end',
							'@lg': {
								justifyContent: 'start',
							},
							'@smMax': {
								paddingTop: '40px',
							},
						}}
					>
						<Text
							css={{
								fontSize: '13px',
								lineHeight: '$2',
								color: '$custom35',
								fontWeight: '$normal',
								paddingTop: '12px',
								letterSpacing: '0.2px',
								textAlign: 'start',
								'@lg': {
									paddingTop: '24px',
								},
							}}
						>
							On clicking below, I agree to receive important information sent
							via WhatsApp and SMS.
						</Text>
						<Button
							primary
							onClick={() => {
								if (otp.length === 6) {
									requestCallBack()
								}
							}}
							disabled={otp.length !== 6}
							css={{
								// backgroundColor: '$custom32',
								minHeight: '48px',
								color: '$white',
								fontSize: '15px',
								fontWeight: '$bold',
								marginTop: '24px',
								borderRadius: '$4',
								'@lg': {
									height: '52px',
									marginTop: '10px',
								},
							}}
						>
							Place Request
						</Button>
					</Column>
				</Column>
			)
			break
		case SHOW_SUBMIT_SUCCESS:
			modelContent = (
				<Column
					align="center"
					justify="center"
					css={{
						width: '100%',
						rowGap: '20px',

						'@smMax': {
							paddingTop: '120px',
						},
					}}
				>
					<StyledImage
						src={
							status.isError
								? 'https://oceanjar-new.s3.ap-south-1.amazonaws.com/cbr-error.png'
								: 'https://oceanjar-new.s3.ap-south-1.amazonaws.com/cbr-success.png'
						}
						width="96px"
						height="96px"
						css={{
							'@lg': {
								borderTopLeftRadius: '$2',
								borderBottomLeftRadius: '$2',
							},
						}}
					/>
					<Column
						css={{
							rowGap: '4px',
							width: '80%',
						}}
					>
						<Text
							css={{
								fontSize: '18px',
								fontWeight: 'bold',
								color: '$grey12',
								lineHeight: '24px',
								textAlign: 'center',
							}}
						>
							{status.title}{' '}
						</Text>
						<Text
							css={{
								fontSize: '16px',
								color: '$grey12',
								lineHeight: '24px',
								textAlign: 'center',
							}}
						>
							{status.message}
						</Text>
					</Column>

					<Button
						primary
						css={{
							width: '224px',
							height: '52px',
							// backgroundColor: '#00B277',
							fontSize: '16px',
							fontWeight: '$bold',
							cursor: 'pointer',
							borderRadius: '$4',
							marginTop: '$3',
						}}
						onClick={() => {
							if (status.isError) {
								setShowSubmitted(false)
								clearAllIntervals()
								backToCBR()
								setValue({
									email: '',
								})
							} else {
								onClose()
							}
						}}
					>
						{status.isError ? 'Okay, go back!' : 'Okay, Got it!'}
					</Button>
				</Column>
			)
			break
		default:
			modelContent = (
				<Column
					css={{
						rowGap: '16px',
						flex: 1,
					}}
				>
					<Row justify="between">
						<Text
							css={{
								width: '85%',
								fontSize: '18px',
								fontWeight: '$bold',
								lineHeight: '24px',
								color: '$grey12',
								marginBottom: '$4',
							}}
						>
							Have a question? Enter your details
						</Text>
						<CloseLine
							width={20}
							height={20}
							cursor="pointer"
							onClick={onClose}
							css={{
								marginTop: '$1',

								'@smMax': {
									display: 'none',
								},
							}}
						/>
					</Row>

					<Input
						disabled={isLoggedIn && name.length}
						placeholder={'Your name'}
						onChange={e => setValue({ name: e })}
						value={name}
						inputProps={{
							css: {
								backgroundColor: '$white',
								height: '52px',
								borderRadius: '$2',
							},
						}}
					/>

					<Row
						gap="2"
						css={{
							width: '100%',
						}}
					>
						<Input
							placeholder={'ISD'}
							value={countryPhoneCode}
							onChange={e => setValue({ countryPhoneCode: e })}
							wrapperProps={{
								css: {
									width: '60px',
									height: '52px',
								},
							}}
							inputProps={{
								css: {
									fontSize: '$2',
									backgroundColor: '$white',
									borderRadius: '$2',
								},
							}}
							disabled={
								isLoggedIn &&
								isValidMobileNumber(countryPhoneCode + mobileNumber)
							}
						/>
						<Input
							placeholder={'Enter your mobile number'}
							value={mobileNumber}
							onChange={e => {
								trackEvent(EVENT_MOBILE_NUMBER_RCB_CLICKED, {
									CountryCode: countryPhoneCode,
									MobileNumber: e,
								})
								setValue({ mobileNumber: e })
							}}
							wrapperProps={{
								css: {
									flex: 1,
									height: '52px',
								},
							}}
							inputProps={{
								css: {
									backgroundColor: '$white',
									borderRadius: '$2',
								},
								type: 'number',
								pattern: '^d{10}$',
								max: 9999999999,
								min: 1,
								'-moz-appearance': 'textfield',
								'&::-webkit-outer-spin-button': {
									'-webkit-appearance': 'none',
									margin: 0,
								},
								'&::-webkit-inner-spin-button': {
									'-webkit-appearance': 'none',
									margin: 0,
								},
							}}
							disabled={
								isLoggedIn &&
								isValidMobileNumber(countryPhoneCode + mobileNumber)
							}
						/>
					</Row>

					<Input
						placeholder={'Email Address'}
						value={email}
						onChange={e => setValue({ email: e })}
						wrapperProps={{
							css: {
								height: '52px',
							},
						}}
						inputProps={{
							css: {
								backgroundColor: '$white',
								borderRadius: '$2',
							},
						}}
						disabled={
							isLoggedIn && userDetails?.email ? emailValidation(email) : false
						}
					/>

					<Button
						primary
						// disabled={!validation}
						css={{
							height: '52px',
							fontSize: '15px',
							fontWeight: 600,
							// cursor: !validation ? 'not-allowed' : 'pointer',
							borderRadius: '$4',
							marginTop: '$4',
						}}
						onClick={() => {
							if (validation) {
								if (isLoggedIn) {
									requestCallBack()
								} else {
									triggerMobileOTP()
								}
							}
						}}
					>
						{ctaText || 'Request callback'}
					</Button>
				</Column>
			)
			break
	}

	const rightSideContent = (
		<Flex
			css={{
				width: '100%',
				gap: '24px',
				'@lg': {
					height: '100%',
					padding: '32px',
				},
				'@smMax': {
					marginTop: '24px',
					paddingLeft: '$4',
					paddingRight: '$4',
				},
			}}
		>
			{modelContent}
		</Flex>
	)

	return (
		<Dialog.Root
			open={visible}
			onOpenChange={showSubmitted ? onClose : () => null}
			defaultOpen
		>
			<Dialog.Content
				css={{
					padding: 0,
					height: '100%',
					width: '100%',
					'& > button': {
						display: 'none',
					},

					'@lg': {
						display: 'flex',
						height: '560px',
						width: '688px',
						borderRadius: '16px',
					},

					'@smMax': {
						all: 'revert',
						backgroundColor: '$white',
						position: 'fixed',
						top: 0,
						bottom: 0,
						left: 0,
						right: 0,
						outline: 0,
					},
				}}
			>
				<Box
					css={{
						backgroundColor: '$custom31',
						borderTopLeftRadius: '16px',
						borderBottomLeftRadius: '16px',
						position: 'relative',

						'@smMax': {
							display: 'none',
						},
					}}
				>
					<StyledImage
						src={getImgIXUrl(
							'https://oceanjar-new.s3.ap-south-1.amazonaws.com/images/web_app/gcm/v1/cbr_desk.png',
						)}
						width="296px"
						css={{
							'@lg': {
								borderTopLeftRadius: '$2',
								borderBottomLeftRadius: '$2',
								position: 'relative',
							},
						}}
					/>
					{/* {modalState !== OTP && (
						<Row
							css={{
								position: 'absolute',
								bottom: '120px',
								left: '62px',
								gap: '6px',
							}}
						>
							<Text
								css={{
									fontSize: '14px',
									color: '$custom45',
									'@smMax': {
										display: 'none',
									},
								}}
							>
								Call us at
							</Text>
							<a href={`tel:${websiteContactNumber}`}>
								<Text
									as="a"
									css={{
										fontSize: '14px',
										fontWeight: 600,
										color: '$primary06',
										'@smMax': {
											display: 'none',
										},
									}}
								>
									{websiteContactNumber}
								</Text>
							</a>
						</Row>
					)} */}
				</Box>

				{(modalState !== OTP || showSubmitted) && (
					<Box
						css={{
							backgroundColor: '#042a45',
							position: 'relative',
							'@lg': {
								display: 'none',
							},
						}}
					>
						<StyledImage
							src={getImgIXUrl(
								'https://oceanjar-new.s3.ap-south-1.amazonaws.com/images/web_app/gcm/v1/cbr_mobile.png',
							)}
							width="100%"
						/>

						<CloseLine
							width={20}
							height={20}
							cursor="pointer"
							onClick={onClose}
							css={{
								position: 'absolute',
								top: '27px',
								right: '27px',
								fill: '$white',
							}}
						/>
					</Box>
				)}

				{rightSideContent}
			</Dialog.Content>
		</Dialog.Root>
	)
}

export default CbrWrapper
